import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/*
 * Disponibilitatea aplicatiilor de business.
 *
 *   GET /business-apps/availability             procentul pe 30 de zile, pe aplicatie (lista)
 *   GET /business-apps/{id}/availability?days=  fisa: zilele si totalurile
 *
 * O aplicatie e `down` cand un rol esential (baza de date, server de aplicatie,
 * web) nu mai are nicio componenta in picioare, `degraded` cand una din rol a
 * cazut dar mai e alta. Caderile vin din monitorizarea externa si din
 * alarmele critice; statiile nu conteaza.
 */

export type AvailabilityState = 'up' | 'degraded' | 'down' | 'nodata'

export type AvailabilityTotals = {
  /** Procent din timpul cunoscut (up + degraded) / (up + degraded + down); null = fara date. */
  availability: number | null
  minutesUp: number
  minutesDegraded: number
  minutesDown: number
  minutesNoData: number
  outages: number
  longestDownMinutes: number
}

export type AvailabilityDay = {
  day: string
  state: AvailabilityState
  /** Socotita pe loc (azi, sau o zi pe care jobul n-a scris-o inca). */
  live: boolean
  minutesUp: number
  minutesDegraded: number
  minutesDown: number
  minutesNoData: number
  outages: number
  longestDownMinutes: number
  availability: number | null
  componentsMonitored: number
}

export type AppAvailabilityPage =
  | { ready: false; message: string }
  | { ready: true; window: number; from: string; to: string; days: AvailabilityDay[]; totals: AvailabilityTotals }

export type AvailabilityOverview =
  | { ready: false; message: string; apps: [] }
  | { ready: true; window: number; from: string; to: string; apps: (AvailabilityTotals & { appId: number })[] }

export function appAvailabilityQuery(id: number, days = 30) {
  return queryOptions({
    queryKey: ['business-apps', 'availability', id, days],
    queryFn: () => apiGet<AppAvailabilityPage>(`/business-apps/${id}/availability?days=${days}`),
  })
}

export function availabilityOverviewQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'availability', 'list'],
    queryFn: () => apiGet<AvailabilityOverview>('/business-apps/availability'),
  })
}

/** 1234 minute -> "20h 34m"; 0 -> "0m". */
export function formatMinutes(minutes: number): string {
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

/** 99.9876 -> "99.99%"; 100 -> "100%"; null -> "-". */
export function formatPercent(value: number | null): string {
  if (value === null) return '-'
  if (value >= 100) return '100%'
  return `${(Math.floor(value * 100) / 100).toFixed(2)}%`
}
